import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { API_ENDPOINT_CREATE } from '../../config/constants';

/**
 * SECURITY NOTE: Microsoft Graph Token Logging
 * ============================================
 * Several console.log statements that expose Microsoft Graph tokens have been commented out
 * throughout this service for production security. These include:
 * 
 * 1. Token acquisition confirmations (lines ~152, ~211, ~345)
 * 2. Token payload details (audience, scopes, roles, app ID) (lines ~462-476)
 * 3. Raw token output for debugging/testing (lines ~485-490)
 * 
 * These logs are useful for local debugging but MUST remain commented out in production
 * to prevent sensitive authentication tokens from being exposed in browser console logs.
 * 
 * To enable for local debugging: Search for "SECURITY:" comments and uncomment temporarily.
 */

export interface EmailValidationResult {
  isValid: boolean;
  exists: boolean;
  displayName?: string;
  errorMessage?: string;
}

export interface UserSearchResult {
  displayName: string;
  email: string;
  userPrincipalName: string;  // Corporate login email (always present from API)
}

@Injectable({
  providedIn: 'root'
})
export class EmailValidationService {
  // Microsoft Graph API endpoint for user lookup
  private readonly graphApiUrl = 'https://graph.microsoft.com/v1.0';
  
  // Cache to avoid redundant API calls
  private validationCache = new Map<string, EmailValidationResult>();
  
  // Track in-flight requests to prevent duplicate calls
  private inFlightRequests = new Map<string, Observable<EmailValidationResult>>();
  
  // Cache Graph token to avoid popup blinking on every validation
  private cachedGraphToken: string | null = null;
  private tokenExpiryTime: number | null = null;

  constructor(
    private http: HttpClient,
    private oauthService: OAuthService
  ) {}

  /**
   * Validates if an email belongs to a corporate user in Azure AD
   * @param email The email address to validate
   * @returns Observable with validation result
   */
  validateCorporateEmail(email: string): Observable<EmailValidationResult> {
    console.log('📧 Validating email:', email);
    
    // Trim and lowercase for consistency
    const normalizedEmail = email.trim().toLowerCase();
    
    // Basic email format validation
    if (!this.isValidEmailFormat(normalizedEmail)) {
      console.log('❌ Invalid email format');
      return of({
        isValid: false,
        exists: false,
        errorMessage: 'Invalid email format'
      });
    }

    // Check cache first
    if (this.validationCache.has(normalizedEmail)) {
      console.log('✅ Found in cache');
      return of(this.validationCache.get(normalizedEmail)!);
    }

    // Check if request is already in flight
    if (this.inFlightRequests.has(normalizedEmail)) {
      console.log('⏳ Request already in flight, reusing...');
      return this.inFlightRequests.get(normalizedEmail)!;
    }

    console.log('🔄 Making new API request...');
    
    // Make the API call
    const request$ = this.checkUserInAzureAD(normalizedEmail).pipe(
      map(result => {
        // Cache successful results
        this.validationCache.set(normalizedEmail, result);
        this.inFlightRequests.delete(normalizedEmail);
        console.log('💾 Cached result:', result);
        return result;
      }),
      catchError(error => {
        this.inFlightRequests.delete(normalizedEmail);
        console.error('❌ Email validation error:', error);
        return of({
          isValid: true, // Allow email format but mark as not found
          exists: false,
          errorMessage: 'Unable to verify user'
        });
      })
    );

    this.inFlightRequests.set(normalizedEmail, request$);
    return request$;
  }

  /**
   * Validates multiple emails at once
   * @param emails Array of email addresses
   * @returns Observable with array of validation results
   */
  validateMultipleEmails(emails: string[]): Observable<EmailValidationResult[]> {
    const validations = emails.map(email => this.validateCorporateEmail(email));
    // This will execute all validations and combine results
    return new Observable(observer => {
      let completed = 0;
      const results: EmailValidationResult[] = new Array(emails.length);
      
      validations.forEach((validation$, index) => {
        validation$.subscribe(result => {
          results[index] = result;
          completed++;
          if (completed === emails.length) {
            observer.next(results);
            observer.complete();
          }
        });
      });
    });
  }

  /**
   * Search for users by name or partial email match
   * @param query Search query (name or partial email)
   * @returns Observable with array of matching users
   */
  searchUsers(query: string): Observable<UserSearchResult[]> {
    console.log('🔍 Searching users:', query);
    
    const normalizedQuery = query.trim();
    
    if (!normalizedQuery || normalizedQuery.length < 2) {
      return of([]);
    }

    // Check if user is authenticated with OAuth (for backend auth)
    const hasOAuthToken = this.oauthService.hasValidAccessToken();
    if (!hasOAuthToken) {
      console.warn('⚠️ No valid OAuth token for backend authentication.');
      return of([]);
    }

    return new Observable(observer => {
      // Get Graph token via popup with correct scope/audience
      this.getGraphTokenViaIframe().subscribe({
        next: (graphToken) => {
          // SECURITY: Don't log token acquisition in production
          // console.log('✅ Graph token acquired for user search');
          
          // Call backend Graph proxy endpoint for user search
          const backendUrl = `${API_ENDPOINT_CREATE}/microsoft-graph-search-users`;
          
          const headers = new HttpHeaders({
            'X-Graph-Token': graphToken,
            'Content-Type': 'application/json'
          });

          this.http.post<any>(backendUrl, { query: normalizedQuery }, { headers }).subscribe({
            next: (response) => {
              console.log('✅ User search successful:', response);
              observer.next(response.users || []);
              observer.complete();
            },
            error: (error) => {
              console.error('❌ User search error:', error);
              observer.next([]);
              observer.complete();
            }
          });
        },
        error: (error) => {
          console.error('❌ Failed to get Graph token for search:', error);
          observer.next([]);
          observer.complete();
        }
      });
    });
  }

  /**
   * Check if user exists in Azure AD using backend API proxy
   * Backend acts as a proxy to avoid browser CORS blocking
   * Acquires separate Graph token via iframe for Graph API calls
   * @param email User email to check
   * @returns Observable with validation result
   */
  private checkUserInAzureAD(email: string): Observable<EmailValidationResult> {
    console.log('📡 Step 1: Checking OAuth authentication...');
    
    // Check if user is authenticated with OAuth (for backend auth)
    const hasOAuthToken = this.oauthService.hasValidAccessToken();
    if (!hasOAuthToken) {
      console.warn('⚠️ No valid OAuth token for backend authentication. User may not be fully logged in yet.');
      return of({
        isValid: true,
        exists: false,
        errorMessage: 'Authentication in progress. Please wait...'
      });
    }
    
    console.log('📡 Step 2: Getting Graph API token via iframe...');

    return new Observable(observer => {
      // Get Graph token via iframe with correct scope/audience
      this.getGraphTokenViaIframe().subscribe({
        next: (graphToken) => {
          // SECURITY: Don't log token acquisition in production
          // console.log('✅ Step 3: Graph token acquired!');
          this.logTokenDetails(graphToken);
          
          console.log('📡 Step 4: Calling backend Graph proxy...');
          
          // Call backend Graph proxy endpoint using same pattern as other API calls
          const backendUrl = `${API_ENDPOINT_CREATE}/microsoft-graph-validate-email`;
          
          console.log('📡 Backend URL:', backendUrl);
          
          // Set custom headers including X-Graph-Token
          // Note: Interceptor will automatically add Authorization header (Bearer/Basic Auth)
          const headers = new HttpHeaders({
            'X-Graph-Token': graphToken,
            'Content-Type': 'application/json'
          });

          // Use http.post with custom headers
          // The interceptor will add backend authentication (Bearer or Basic Auth)
          this.http.post<any>(backendUrl, { email }, { headers }).subscribe({
            next: (response) => {
              console.log('✅ Step 5: Backend validation successful:', response);
              observer.next({
                isValid: true,
                exists: response.exists,
                displayName: response.displayName || email,
                errorMessage: undefined
              });
              observer.complete();
            },
            error: (error) => {
              console.error('❌ Backend API error:', error);
              
              if (error.status === 404) {
                observer.next({
                  isValid: true,
                  exists: false,
                  errorMessage: 'User not found in organization'
                });
              } else if (error.status === 401) {
                observer.next({
                  isValid: true,
                  exists: false,
                  errorMessage: 'Authentication required'
                });
              } else {
                observer.next({
                  isValid: true,
                  exists: false,
                  errorMessage: 'Unable to verify user'
                });
              }
              observer.complete();
            }
          });
        },
        error: (error) => {
          console.error('❌ Failed to get Graph token:', error);
          observer.next({
            isValid: true,
            exists: false,
            errorMessage: 'Failed to authenticate with Microsoft Graph'
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Get Microsoft Graph API token using popup window
   * This uses implicit flow with interactive authentication to acquire a token with correct Graph audience
   * Token is cached in memory to avoid popup blinking on every validation
   * @returns Observable with Graph API token
   */
  private getGraphTokenViaIframe(): Observable<string> {
    // Check if we have a valid cached token
    if (this.cachedGraphToken && this.tokenExpiryTime && Date.now() < this.tokenExpiryTime) {
      console.log('✅ Using cached Graph token (expires in ' + Math.round((this.tokenExpiryTime - Date.now()) / 1000) + 's)');
      return of(this.cachedGraphToken);
    }

    return new Observable(observer => {
      console.log('🔐 Opening popup for Graph token acquisition...');

      // Calculate popup window position (centered)
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Open popup window
      const popup = window.open(
        '',
        'GraphAuthPopup',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (!popup) {
        observer.error(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      const tenantId = '0ae51e19-07c8-4e4b-bb6d-648ee58410f4';
      const clientId = '2839b914-5970-4a0e-adaa-349ae4eb7bb1';
      const graphScope = 'https://graph.microsoft.com/User.ReadBasic.All';
      
      // Use the redirect URI that's already registered: http://localhost:4200/ui
      const redirectUri = `${window.location.origin}/ui`;

      // Construct OAuth URL for implicit flow with interactive authentication
      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize` +
        `?client_id=${clientId}` +
        `&response_type=token` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&scope=${encodeURIComponent(graphScope)}`; // +
        // `&prompt=consent`; // Interactive authentication (allows consent)

      console.log('🔗 Popup auth URL:', authUrl);

      let timeoutId: any;
      let checkInterval: any;

      // Listen for message from popup
      const messageHandler = (event: MessageEvent) => {
        // Accept messages from same origin
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data && event.data.type === 'graph-token') {
          cleanup();

          if (event.data.token) {
            // SECURITY: Don't log token receipt in production
            // console.log('✅ Graph token received from popup');
            
            // Cache the token (tokens typically expire in 1 hour, cache for 50 minutes to be safe)
            this.cachedGraphToken = event.data.token;
            this.tokenExpiryTime = Date.now() + (50 * 60 * 1000); // 50 minutes
            console.log('💾 Token cached until:', new Date(this.tokenExpiryTime).toLocaleTimeString());
            
            observer.next(event.data.token);
            observer.complete();
          } else if (event.data.error) {
            console.error('❌ Popup returned error:', event.data.error);
            observer.error(new Error(event.data.error));
          } else {
            console.error('❌ No token in popup response');
            observer.error(new Error('No token received from popup'));
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Cleanup function
      const cleanup = () => {
        window.removeEventListener('message', messageHandler);
        clearTimeout(timeoutId);
        clearInterval(checkInterval);
        if (popup && !popup.closed) {
          popup.close();
        }
      };

      // Check if popup was closed by user
      checkInterval = setInterval(() => {
        if (popup.closed) {
          console.warn('⚠️ Popup closed by user');
          cleanup();
          observer.error(new Error('Authentication cancelled by user'));
        }
      }, 500);

      // Set timeout (60 seconds)
      timeoutId = setTimeout(() => {
        cleanup();
        console.error('❌ Graph token acquisition timeout');
        observer.error(new Error('Token acquisition timeout'));
      }, 60000);

      // Navigate popup to auth URL
      popup.location.href = authUrl;
    });
  }

  /**
   * Basic email format validation
   * @param email Email to validate
   * @returns true if format is valid
   */
  private isValidEmailFormat(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Create an observable that validates email with debounce
   * Useful for real-time validation as user types
   * @param emailSubject$ Subject that emits email values
   * @param debounceMs Debounce time in milliseconds (default 500ms)
   * @returns Observable with validation results
   */
  createDebouncedValidator(
    emailSubject$: Observable<string>, 
    debounceMs: number = 500
  ): Observable<EmailValidationResult | null> {
    return emailSubject$.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      switchMap(email => {
        if (!email || email.trim().length === 0) {
          return of(null);
        }
        return this.validateCorporateEmail(email);
      })
    );
  }

  /**
   * Clear the validation cache
   * Useful when user authentication state changes
   */
  clearCache(): void {
    this.validationCache.clear();
    this.inFlightRequests.clear();
  }

  /**
   * Remove specific email from cache
   * @param email Email to remove from cache
   */
  removeFromCache(email: string): void {
    const normalizedEmail = email.trim().toLowerCase();
    this.validationCache.delete(normalizedEmail);
  }

  /**
   * Decode and log JWT token details for debugging
   * @param token JWT token to decode
   */
  private logTokenDetails(token: string): void {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('❌ Invalid JWT token format');
        return;
      }

      const payload = JSON.parse(atob(parts[1]));
      
      // SECURITY: Token details should NOT be logged in production - sensitive information
      // This debug block exposes token payload including audience, scopes, roles, app ID
      // Only enable for local debugging, must be commented out before deployment
      /* 
      console.log('🔐 TOKEN DETAILS:');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('Audience (aud):', payload.aud);
      console.log('Scopes (scp):', payload.scp);
      console.log('Roles (roles):', payload.roles);
      console.log('App ID (appid):', payload.appid);
      console.log('Expires:', new Date(payload.exp * 1000).toLocaleString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      */
      
      // Check if it's a Graph API token
      const isGraphToken = payload.aud === '00000003-0000-0000-c000-000000000000' || 
                          (typeof payload.aud === 'string' && payload.aud.includes('graph.microsoft.com'));
      
      /* SECURITY: Don't log token type validation in production
      if (isGraphToken) {
        console.log('✅ This is a Microsoft Graph API token');
      } else {
        console.log('⚠️ This is NOT a Graph API token');
        console.log('   Expected audience: 00000003-0000-0000-c000-000000000000');
        console.log('   Actual audience:', payload.aud);
      }
      
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📋 COPY TOKEN FOR BRUNO:');
      console.log(token);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      */
      
    } catch (error) {
      console.error('❌ Error decoding token:', error);
    }
  }
}
